import * as constants from './Constants'
import { playClickSound } from './Sound'
import { THREE } from 'aframe'
import {iFrameDeviceOrientationControls} from './iFrameDeviceOrientationControls.js'

import Plausible from 'plausible-tracker'


const { trackPageview, trackEvent } = Plausible({
  domain: 'kylie-world.netlify.app',
  trackLocalhost: true
}) 


// Global function used to safely play videos.
// Based on various bits of advice from Stack Overlow about how to handle errors that can arise when playing videos.
// https://stackoverflow.com/questions/69108068/uncaught-in-promise-domexception-the-element-has-no-supported-sources-in-audi
// https://stackoverflow.com/questions/70919067/ncaught-in-promise-domexception-the-element-has-no-supported-sources-while-tr
// https://stackoverflow.com/questions/64474338/audio-player-returns-uncaught-in-promise-domexception-the-element-has-no-su
function safePlay(video) {

  var playPromise = video.play();
  // In browsers that don’t yet support this functionality,
  // playPromise won’t be defined.
  if (playPromise !== undefined) {
      playPromise.then(function() {
      // Automatic playback started!
    }).catch(function(error) {
      console.log(error);
      console.log("video src", video.currentSrc);
      console.log("try to reload");

      video.load();

      var secondPromise = video.play();        
      secondPromise.then(function() {
        // Automatic playback started!
      }).catch(function(error) {
        console.log(error);
        console.log("Simple reload failed - VIDEO PLAY WILL FAIL.");

        /*
        // Had tried this code to do an even harder reset, but not found any situation where this
        // helps 
        caches.open('v1').then(function(cache) {
          cache.delete(video.currentSrc).then(function(response) {
            video.load();
            video.addEventListener("canplaythrough", () => {
              video.play();        
            })                
          });
        })
        */
      })
    });
  }
}

// This is unused when deployed inside an iFrame because gyro permissions are handled outside the iFrame.
export const enableGyro = {
  init() {
    this.permissionGranted = false

    // Browser doesn't support or doesn't require permission to DeviceOrientationEvent API.
    if (typeof DeviceOrientationEvent === 'undefined' || !DeviceOrientationEvent.requestPermission) {
      this.permissionGranted = true
      return
    }
    DeviceOrientationEvent.requestPermission().then((response) => {
      if (response === 'granted') {
        this.el.emit('deviceorientationpermissiongranted')
        this.permissionGranted = true
      } else {
        // no gyro permissions...
        this.permissionGranted = false
      }
    }).catch(console.error)
  },
}

// add to camera *before* look-controls.
export const iFrameGyro = {

  init() {
    console.log("Init iFrame Gyro")
    // Modify DeviceOrientationControls used by look-controls component to use a custom
    // version that works with iFrames.
    THREE.DeviceOrientationControls = iFrameDeviceOrientationControls;

    // Set up iFrame message event handler.
    window.addEventListener('message', this.handleMessage.bind(this), false);

    this.gyroPermissionsResolved = false
  },

  handleMessage(evt) {
    //console.log("Inner received event:", evt)

    if (typeof evt.data !== "string") return;

    var gyroPermissionsResolved = false
    const eventData = JSON.parse(evt.data)
    if (eventData.type === "gyro") {
      const orientEvent = new CustomEvent('iframe-deviceorientation', {detail: eventData})
      //console.log("Inner dispatching event:", orientEvent)
      window.dispatchEvent(orientEvent);
      gyroPermissionsResolved = true
    }
    else if (eventData.type === "blocked") {
      console.log("Reporting Gyro permissions: blocked")
      trackEvent("Gyro Permission", {
        callback: () => console.log("done"),
        props: {
          status: "blocked",
        }
      })
      gyroPermissionsResolved = true
    }    

    if (gyroPermissionsResolved) {

      if (this.el.sceneEl.components['scene-controller'].initialized) {
        if (!this.gyroPermissionsResolved) {
          this.gyroPermissionsResolved = true;
          this.el.emit("gyropermissionsresolved")
        }
      }
    }
  }
}

/* Main component for controlling the overall scene
   Overall lifecycle management / asset loading.
   Hides / shows entities based on "page" attribute.
   Handles initial alignment of 3D space with camera on entry to the room for the 1st time.
   */
export const sceneController = {
  schema: {
    page: {type: 'string'},  /// one of: blank, select or vto
    startevent: {type: 'string', default: 'startscene'},
    target: {type: 'selector'},
    yrotation: {type: 'number', default: 0},
    fader: {type: 'selector', default: '#fader'},
  },

  init() {
    console.log('Initializing scene-controller')

    trackEvent("Initial App Load", {
      callback: () => console.log('track plausible initial app load'),
      props: {
        speed: navigator.connection ? navigator.connection.downlink : undefined,
      }
    })

    this.updatePage()
    this.firstTimeShowingMainRoom = true
    this.lastPageIsVTO = false    

    this.assetsMap = new Map();
    this.videoElementIds = ["introvideo360",
                            "vtovideo360",
                            "video-lips-short",
                            "video-eyes-short",
                            "video-blush-short",
                            "video-plump-short"]
    this.videoElementIds.forEach((id) => {
      this.assetsMap.set(id, false)
    })
        
    this.imageElementIds = ["introvideostill",
                            "balloon-occlusion",
                            "balloon1",
                            "balloon2",
                            "balloon3",
                            "balloon4",
                            "room360", 
                            "subroom360-product", 
                            "subroom360-looks", 
                            "product-basket-icon",
                            "out-of-stock-icon",
                            "product-lips", 
                            "product-eyes", 
                            "product-blush",
                            "product-plump",
                            "product-palette",
                            "glow", 
                            "vto-glow",
                            "benefits-lips", 
                            "benefits-blush", 
                            "benefits-eyes",
                            "benefits-palette-1",
                            "benefits-palette-2",
                            "benefits-palette-3",
                            "benefits-plumping-gloss-1",
                            "benefits-plumping-gloss-2",
                            "benefits-border",
                            "video-border",
                            "looks-classic",
                            "looks-lips",
                            "looks-eyes"]
/*
    this.imageElementIds = []
    const imageNodes = document.querySelectorAll('img');
    for (var ii = 0; ii < imageNodes.length; ii++) {
      if (imageNodes[ii].id) {
        this.imageElementIds.push(imageNodes[ii].id);        
      }
    }*/
    this.imageElementIds.forEach((id) => {
      this.assetsMap.set(id, false)
    })

    this.imageElementLoadingState = this.videoElementIds.map((id) => ({id: id, loaded: false}))    

    this.assetsLoaded = 0;

    this.fuseTimerPopped = this.fuseTimerPopped.bind(this)
    this.introVideoEnded = this.introVideoEnded.bind(this)
    this.introVideoStarted = this.introVideoStarted.bind(this)
    this.gyroPermissionsResolvedFunction = this.gyroPermissionsResolvedFunction.bind(this)
    
    this.introVideo = document.querySelector('#introvideo360')
    this.introVideoSphere = document.querySelector('#intro-videosphere')
    this.introStill = document.querySelector('#intro-still')

    this.vtoVideo = document.querySelector('#vtovideo360')
    this.vtoVideoStarted = this.vtoVideoStarted.bind(this)
    this.vtoVideoEnded = this.vtoVideoEnded.bind(this)

    this.click = this.click.bind(this)
    this.videoEndNotified = false

    this.selectElement = document.querySelector('#select')
    this.cameraElement = document.querySelector('#camera')
    this.cameraRotation = new THREE.Euler()

    this.gyroPermissionsResolved = false
    this.introVideoReady = false

    if (typeof DeviceOrientationEvent === 'undefined' || !DeviceOrientationEvent.requestPermission) {
      // this device does not support gyro event permissions.
      this.gyroPermissionsResolved = true
    }    

    if (this.page === 'intro') {
      // unused...
      // this.startScene = this.startScene.bind(this)
      // this.el.addEventListener('startscene', this.startScene)

      // event listeners not needed when we jump into a non-intro page
      // (e.g. after VTO)
      this.introVideo.addEventListener('ended', this.introVideoEnded)

      console.log('scene-controller: Adding click event listener on window.')
      this.introStill.addEventListener('click', this.click)

      // For iOS, we need to listen for the touchend event on the window.  Click doesn't fire.
      console.log('scene-controller: Adding touchend event listener on window.')
      this.introStill.addEventListener('touchend', this.click)

    }

    const scene = this.el.sceneEl

    /* Overview of assets loading squence...
       A-Frame asset loading begins, but nothing happens.  We timeout after 1msec
       'relaityready' event fires
       At this point asset loading gets triggered by instantiation of entities in the scene.
       We wait for the intro video to load, or a 30 second timeout (whicever comes first)
       We don't worry about other assets - they are small and not needed immediately.
       Just watching the video is a good-enough proxy for when everything is ready to go.
       Intro video loaded ('canplaythrough' event) or 30 second timeout...
       Now we hide the loading screen and display the scene.
       At this point we also request gyro permissions on iOS, and start another 30 second timer.
       User has 30 seconds to click the screen (including accepting gyro permissions on iOS).
       On user click, we begin playing the video.
       After 30 seconds, we cut out the video and jump straight into the experience.  This helps
       to deal with any issues that might arise with user clicks *or* video playback.
       */


    // For operation without 8th wall, need to trigger "realityready" event when loaded...
    scene.addEventListener('loaded', () => {
      // Note - at this point reality is *NOT* ready
      // Asset download does not actually start until this point
      // (due to weirdness around how React initializes the A-FRame scene).
      console.log("tracking plausible page view")
      trackPageview()

      scene.emit("realityready");
    });

    scene.addEventListener('realityready', () => {
      console.log('realityready event')
      const event = new Event('realityready')
      window.dispatchEvent(event)      
    })

    scene.addEventListener('realityerror', () => {
      console.log('realityerror event')
      const event = new Event('realityerror')
      window.dispatchEvent(event)
    })

    // Set up event detection for whether or not gyro events are detected.
    // Assumption that no gyro events detected in 5 seconds => either blocked or there are none (e.g. desktop)
    this.gyroEventsDetected = 0;
    window.addEventListener('deviceorientation', this.reportDeviceOrientation.bind(this));
    window.addEventListener('iframe-deviceorientation', this.reportIframeDeviceOrientation.bind(this));  
    
    scene.addEventListener('gyropermissionsresolved', this.gyroPermissionsResolvedFunction)

    this.el.addEventListener('all-assets-ready', () => {
  
      this.introVideoReady = true
      if (this.gyroPermissionsResolved) {

        this.declareIntroVideoReady()
      }
    })

    this.videoElementIds.forEach((id) => {
      const video = document.getElementById(id);
      if (video.id &&
          video.videoHeight > 0) {
        // Video already loaded.
        this.declareAssetLoaded(video.id)
      }
      else {
        video.addEventListener('canplaythrough', (evt) => {
          const id = evt.currentTarget.id;
          this.declareAssetLoaded(id)
        })
      }
      // iOS requires explicit load() call for 'canplaythrough' event to fire.
      // https://stackoverflow.com/questions/49792768/js-html5-audio-why-is-canplaythrough-not-fired-on-ios-safari      
      video.load();
    })

    this.imageElementIds.forEach((id) => {
      //console.log("id:", id)
      const image = document.getElementById(id);
      if (image.id &&
          image.naturalHeight > 0) {
          // Image already loaded.
          this.declareAssetLoaded(image.id)
      }
      else {
        image.addEventListener('load', (evt) => {
          const id = evt.currentTarget.id
          this.declareAssetLoaded(id)
        })
      }      
    })
  },

  reportIframeDeviceOrientation(e) {

    if (this.gyroEventsDetected === 5) {
      // getting gyro events - report to plausible.
      console.log("Reporting Gyro permissions: granted")
      trackEvent("Gyro Permission", {
        callback: () => console.log("done"),
        props: {
          status: "granted",
        }
      })
    }
    this.gyroEventsDetected++;
  },

  reportDeviceOrientation(e) {
    if (this.gyroEventsDetected === 5) {
      // getting gyro events - report to plausible.
      console.log("Reporting Gyro permissions: automatic")
      trackEvent("Gyro Permission", {
        callback: () => console.log("done"),
        props: {
          status: "automatic",
        }
      })
    }
    this.gyroEventsDetected++;

  },

  declareAssetLoaded(id) {
    if (!this.assetsMap.get(id)) {
      this.assetsLoaded ++;
      this.assetsMap.set(id, true)
      if (this.assetsLoaded >= this.videoElementIds.length + this.imageElementIds.length) {
        this.el.emit("all-assets-ready")
      }
    }
  },

  declareIntroVideoReady() {
    console.log("INTRO VIDEO LOADED")
    const event = new Event('introvideoready')
    window.dispatchEvent(event)      

    // having trouble with playing video on iOS, so set a 30 second fuse on the video and kill it
    // if it's not ended
    // Note that If we skip the initial screen tap, we won't get permissions for gyro.
    setTimeout(this.fuseTimerPopped, 120000)

    if (!this.userClicked) {
      this.showById('enter-prompt')
    }
  },

  gyroPermissionsResolvedFunction() {

    this.gyroPermissionsResolved = true;
    if (this.introVideoReady) {
      this.declareIntroVideoReady()
    }

    // once gyro permisions resolved, we no longer need the device-orientation-permission-ui.
    this.el.sceneEl.removeAttribute('device-orientation-permission-ui')
  },

  updatePage() {
    // Page can come from one of 3 sources.
    // If provided in parameters, we use that.
    // If none provided, we look to our external communication channel to React through
    // a parent element in the DOM.
    // If still nothing found, we default to "intro" (fallback safety mechanism, should never happen).

    const dataDiv = document.querySelector('#aframe-scene-data')

    if (this.data.page) {
      this.page = this.data.page
    } else if (dataDiv) {
      this.page = dataDiv.dataset.page
    } else {
      this.page = 'intro'
    }
  },

  update() {
    this.updatePage()
    console.log(`Updated page:${this.page}`)

    const {fov} = document.querySelector('[camera]').components.camera.data
    console.log(`FOV: ${fov}`)

    // whether or not to align the room.
    var alignRoom = false;

    switch (this.page) {
      case 'blank':
        // no op.
        // screen content may not actually be blank, but will be hidden by 2D scene anyway.
        break

      case 'intro':
        // Hiding everything but the videosphere ensures nothing is clickable
        // that shouldn't be yet.
        alignRoom = true;
        this.hideEverything()
        this.showById('intro-video')

        break

      // Show the main room
      // or... Show the main room, but without re-aligning.
      case 'main-room-align':      
      case 'main-room':
        
        // Can't put this code before case 'main-room' as it triggers a missing 'break' warning.
        if (this.page === 'main-room-align') {
          // Given new layout, intro video etc.
          // I believe we no longer want to align room - allows for smoother transiiton video -> room.
          //alignRoom = true;
        }
        
        this.hideEverything()

        if (false) { // was: this.firstTimeShowingMainRoom  - but this is delaying display of layers behind...
          console.log('Fading in main room')
          this.data.fader.removeAttribute('animation__fadein')
          this.data.fader.setAttribute('animation__fadein',
            {
              property: 'opacity',
              from: 1,
              to: 0,
              dur: 1000,
              autoplay: true,
            })
          // give a slight buffer for animation to complete before removing
          // visibility.
          // Don't keep a layer around with opacity = 0 as it can cause camera
          // glitches.
          setTimeout(() => this.data.fader.setAttribute('visible', false), 1500)
        }
        this.firstTimeShowingMainRoom = false

        console.log("Showing Main Room");
        this.showById('main-room')
        this.showById('look-around-prompt-mini')
        break

      case 'looks-room-classic':
      case 'looks-room-eyes':
      case 'looks-room-lips':
        this.hideEverything()

        const classic = document.getElementById("looksroom-classic-wall");
        const eyes = document.getElementById("looksroom-eyes-wall")
        const lips = document.getElementById("looksroom-lips-wall")

        // switch entities around to put the focussed look in fron of the camera.
        switch (this.page) {
          case 'looks-room-eyes':
            classic.setAttribute('rotation', "0 -90 0");
            eyes.setAttribute('rotation', "0 0 0");
            lips.setAttribute('rotation', "0 90 0");
            break

          case 'looks-room-lips':
            eyes.setAttribute('rotation', "0 -90 0");
            lips.setAttribute('rotation', "0 0 0");
            classic.setAttribute('rotation', "0 90 0");
            break

          case 'looks-room-classic':
            lips.setAttribute('rotation', "0 -90 0");
            classic.setAttribute('rotation', "0 0 0");
            eyes.setAttribute('rotation', "0 90 0");
            break

          default:
            break
        }

        // Show the looks room.
        this.showById('looks-room')
        this.showById('look-around-prompt-mini')
        break

      case 'lips-room':
        this.hideEverything()
        // Show the looks room.
        this.showById('lips-room')
        this.showById('look-around-prompt-mini')
        // video entites have weird parenting due to inherit-transform, so need to have their
        // visibility explicitly managed.
        this.showById('video-lips-entity')
        break

      case 'eyes-room':
        this.hideEverything()
        // Show the eyes room.
        this.showById('eyes-room')
        this.showById('eyes-video')
        this.showById('look-around-prompt-mini')
        // video entites have weird parenting due to inherit-transform, so need to have their
        // visibility explicitly managed.
        this.showById('video-eyes-entity')
        break

      case 'blush-room':
        this.hideEverything()
        // Show the looks room.
        this.showById('blush-room')
        this.showById('blush-video')
        this.showById('look-around-prompt-mini')
        // video entites have weird parenting due to inherit-transform, so need to have their
        // visibility explicitly managed.
        this.showById('video-blush-entity')
        break

      case 'plump-room':
        this.hideEverything()
        // Show the plump room.
        this.showById('plump-room')
        this.showById('look-around-prompt-mini')
        break

      case 'palette-room':
        this.hideEverything()
        // Show the palette room.
        this.showById('palette-room')
        this.showById('look-around-prompt-mini')
        break

      case 'vto-entry':

        // Make videoshpere visible - won't actually be shown until sky is removed,
        // which happens once we observe the first frame of the video.
        this.showById('vto-videosphere')

        this.vtoVideo.addEventListener('timeupdate', this.vtoVideoStarted)
        this.vtoVideo.addEventListener('ended', this.vtoVideoEnded)

        const video = this.vtoVideo
        if (video) {
          safePlay(video)
        }
        else {
          console.warn("Failed to get video to play for entry to VTO")
        }
        break

      case 'vto':
        this.hideEverything()
        break

      default:
        console.log(`Unexpected page: ${this.data.page}`)
        break
    }

    // When required, rotate the "select" space to align with the camera.

    if (alignRoom) {

      // take the Y rotation of the camera.  We take this as the primary axis of rotation.
      this.cameraRotation.setFromQuaternion(this.cameraElement.object3D.quaternion, 'YXZ')
      console.log(`Camera rotation: ${this.cameraRotation.y * 180 / Math.PI}`)
      console.log(`Select rotation: ${this.selectElement.object3D.rotation.y * 180 / Math.PI}`)
      console.log(`Requested Y Rotation: ${this.data.yrotation}`)

      // set the select Element Euler based on this (ok to use Euler as we never rotate this in more
      // than one axis.
      const yRot = this.data.yrotation ? this.data.yrotation : 0;
      this.selectElement.object3D.rotation.y = this.cameraRotation.y +
                                              (yRot * Math.PI / 180)
                                              
      this.selectElement.object3D.rotation.x = 0
      this.selectElement.object3D.rotation.z = 0
      console.log(`Updated select rotation x: ${this.selectElement.object3D.rotation.x * 180 / Math.PI}`)
      console.log(`Updated select rotation y: ${this.selectElement.object3D.rotation.y * 180 / Math.PI}`)
      console.log(`Updated select rotation z: ${this.selectElement.object3D.rotation.z * 180 / Math.PI}`)

    }

    // and make sure anything that depends on cart state is up to date.
    this.el.emit('cartchanged')
  },

  hideEverything() {
    this.hideById('main-room')
    this.hideById('intro-video')
    this.hideById('looks-room')
    this.hideById('lips-room')
    this.hideById('blush-room')
    this.hideById('plump-room')
    this.hideById('palette-room')
    this.hideById('eyes-room')
    this.hideById('enter-prompt')
    this.hideById('look-around-prompt-mini')

    // video entites have weird parenting due to inerit-transform, so need to have their
    // visibility explicitly managed.
    this.hideById('video-lips-entity')
    this.hideById('video-eyes-entity')
    this.hideById('video-blush-entity')


    // broadcast a request to stop any videos playing.
    this.el.emit('stopvideos')
  },

  hideById(name) {
    const element = document.querySelector(`#${name}`)
    element.setAttribute('visible', false)

    this.makeDescendentsUnclickable(element)
  },

  showById(name) {
    const element = document.querySelector(`#${name}`)
    element.setAttribute('visible', true)

    this.makeDescendentsClickable(element)
  },

  makeDescendentsClickable(element) {
    this.setDescendentsClickableDisabled(element, false)
  },

  makeDescendentsUnclickable(element) {
    this.setDescendentsClickableDisabled(element, true)
  },

  setDescendentsClickableDisabled(element, state) {
    if (element.hasAttribute('clickable')) {
      element.setAttribute('clickable', {'disabled': state})
    }
    if (element.hasAttribute('click-to-add-to-cart')) {
      element.setAttribute('click-to-add-to-cart', {'disabled': state})
    }
    if (element.hasAttribute('click-to-show-product')) {
      element.setAttribute('click-to-show-product', {'disabled': state})
    }

    const children = Array.from(element.children)
    children.forEach(el => this.setDescendentsClickableDisabled(el, state))
  },

  startScene() {
    // this.data.target.emit('zoom')
    // setTimeout(() => this.data.target.emit('objectsappear'), 2000)
  },

  click() {

    playClickSound()

    // making video visible before play has completely started results in a black flash.
    // we should improve this...
    console.log('Click detected - requesting gyro permissions...')
    if (typeof DeviceOrientationEvent !== 'undefined' && DeviceOrientationEvent.requestPermission) {
      DeviceOrientationEvent.requestPermission().then((response) => {
        if (response === 'granted') {
          console.log('Permission granted')
        } else {
          // no gyro permissions...
          console.log('Permission denied')
        }
      }).catch(console.error)
    } else {
      // Browser doesn't support or doesn't require permission to DeviceOrientationEvent API.
      
      if (typeof DeviceOrientationEvent === 'undefined') {
        console.log('Reporting Gyro Permissions: not required')
        trackEvent("Gyro Permission", {
          callback: () => console.log("done"),
          props: {
            status: "unrequired (e.g. desktop)",
          }
        })
      }
    }

    console.log('Click detected - starting video')
    // make the still it no longer clickable, and remove once video has started.
    // don't remove on "play" event, but only when we confirm the video play has actually begun
    // else we will see a flash of black on screen.
    this.introStill.setAttribute('class', '')
    this.introVideo.addEventListener('timeupdate', this.introVideoStarted)

    const playPromise = this.introVideo.play()

    // pause video as soon as it starts, then restart after 200 msecs.
    // The intention is to avoid missing the very start of the video.
    if (playPromise !== undefined) {
      playPromise.then(() => {

        this.introVideo.pause()
        setTimeout(() => this.introVideo.play(), 200)
      })
      .catch(error => {
        console.error("Play video failed. Error:", error)
      });
    }

    this.introVideoSphere.setAttribute('visible', true)

    // also hide the Enter Prompt.
    this.hideById('enter-prompt')

    // don't listen for any more click / touchend events.
    this.introStill.removeEventListener('click', this.click)
    this.introStill.removeEventListener('touchend', this.click)

    // note that user clicked - so no need to act on fuse timer.
    this.userClicked = true
  },

  introVideoStarted() {

    trackEvent("Intro Video Started", {
      callback: () => console.log("track plausible intro video started", navigator.connection ? navigator.connection.downlink : undefined),
      props: {
        speed: navigator.connection ? navigator.connection.downlink : undefined,
      }
    })

    // make still invisiable once video has started.
    this.introStill.setAttribute('visible', false)
    this.introVideo.removeEventListener('timeupdate', this.introVideoStarted)

  },

  fuseTimerPopped() {
    if (!this.userClicked) {
      // 2 minutes seconds, and user still hasn't clicked - jump straight into scene.
      this.introVideoEnded(null, true)

      // act as if user clicked - don't show prompt to user to tap screen.
      this.userClicked = true
    }
  },

  introVideoEnded(e, fuseFired = false) {
    
    // To tell React the video has ended, we need to raise an event at window scope.
    // but we must only do this once.
    if (!this.videoEndNotified) {

      if (!fuseFired) {
        // I case where video didn't actually end, but instead the fuse timer popped, don't record 
        // a video end event.
        trackEvent("Intro Video Watched", {
            callback: () => console.log("track plausible intro video watched", navigator.connection.downlink),
            props: {
              speed: navigator.connection ? navigator.connection.downlink : undefined
            }
        })
      }

      this.videoEndNotified = true
      const event = new Event('introVideoEnded')
      window.dispatchEvent(event)

      //this.introVideoSphere.setAttribute("visible", false)
    }
  },

  vtoVideoStarted(e) {

    // make video visible once video has started.
    // hide rest of main room.
    this.hideById('main-room-sky')
    this.hideById('vto-glow-effect')
    this.hideById('mainroom-left-wall')
    this.hideById('mainroom-right-wall')
    this.hideById('balloons')
    this.vtoVideo.removeEventListener('timeupdate', this.vtoVideoStarted)

  },

  vtoVideoEnded(e) {

    // remove hide VTO entry video (allowing time for transition / fade-ut)
    setTimeout(() => {
      this.hideById('vto-videosphere')
      this.showById('main-room-sky')
      this.showById('vto-glow-effect')
      this.showById('mainroom-left-wall')
      this.showById('mainroom-right-wall')
      this.showById('balloons')
    }, 1000)

    this.vtoVideo.removeEventListener('ended', this.vtoVideoEnded)

    const event = new Event('vtoVideoEnded')
    window.dispatchEvent(event)
  }
}

// this component applies a class from an HTML entity to an A-Frame entity.
export const classFromSelector = {

  schema: {
    div: {type: 'selector'},
  },

  update() {
    if (this.data.div) {
      this.el.setAttribute('class', this.data.div.className)
    } else {
      console.log('Failed to find reference for A-Frame scene classs in class-from-selector')
    }
  },
}

export const virtualRoom = {
  schema: {
    objectDistance: {type: 'number', default: 1},
    objectGap: {type: 'number', default: 30},
    camera: {type: 'selector', default: '#camera'},
    fader: {type: 'selector', default: '#room-fader'},
  },

  init() {
    console.log('initialized virtualRoom')
    this.bindEventListeners()
    this.addEventListeners()

    // create list of children.  These can be found in the child object with
    // and object-set component (only include "real" children - with an object3D)
    this.objectsContainer = Array.from(this.el.children)
      .filter(child => child.components['object-set'])[0]
    this.childrenArray = Array.from(this.el.children)
      .filter(child => child.object3D)
  },

  bindEventListeners() {
    this.listeners = {
      zoom: this.zoom.bind(this),
      objectsAppear: this.objectsAppear.bind(this),
      fadeIn: this.fadeIn.bind(this),
      fadeOut: this.fadeOut.bind(this),
    }
  },

  addEventListeners() {
    this.el.addEventListener('zoom', this.listeners.zoom)
    this.el.addEventListener('objectsappear', this.listeners.objectsAppear)
    this.el.addEventListener('fadein', this.listeners.fadeIn)
    this.el.addEventListener('fadeout', this.listeners.fadeOut)
  },

  zoom() {
    // autoplay animation directly onto camera.
    this.data.camera.removeAttribute('animation__zoom')
    this.data.camera.setAttribute('animation__zoom',
      {
        property: 'camera.fov',
        from: 100,
        to: 80,
        dur: 2000,
        easing: 'easeOutQuad',
        autoplay: true,
      })
  },

  objectsAppear() {
    // trigger appear event for each child.
    // and make it clickable after a delay (enough to let the "appear" animation complete)
    this.childrenArray.forEach((child) => {
      child.emit('appear')
      setTimeout(() => child.setAttribute('clickable', {disabled: false}), 1000)
    })
  },

  hideRoom() {
    this.el.setAttribute('visible', false)
    this.childrenArray.forEach(child => child.setAttribute('clickable', {disabled: true}))
  },

  showRoom() {
    this.el.setAttribute('visible', true)
    this.childrenArray.forEach(child => child.setAttribute('clickable', {disabled: false}))
  },

  fadeIn() {
    this.showRoom()
    this.data.fader.removeAttribute('animation__fadein')
    this.data.fader.setAttribute('animation__fadein',
      {
        property: 'opacity',
        from: 1,
        to: 0,
        dur: 500,
        autoplay: true,
        delay: 50,
      })
    // give a slight buffer for animation to complete before removing
    // visibility.
    // Don't keep a layer around with opacity = 0 as it can cause camera
    // glitches.
    setTimeout(() => this.data.fader.setAttribute('visible', false), 1000)
  },

  fadeOut() {
    // Note that if called twice, this will only fade out once.
    // fader is invisible by default because if visible with 0 opacity
    // it can cause rendering glitches.
    // 50msec delay before starting fade-out as without this
    // there seems to be an interaction with setting visibility,
    // and we just get a snap-to-black effect.
    this.data.fader.setAttribute('visible', true)
    this.data.fader.removeAttribute('animation__fadeout')
    this.data.fader.setAttribute('animation__fadeout',
      {
        property: 'opacity',
        from: 0,
        to: 1,
        dur: 500,
        autoplay: true,
        delay: 50,
      })
    setTimeout(() => this.hideRoom(), 1750)
  },
}

// Component that handles layout of a set of objects at a set distance from the camera.
export const objectSet = {
  schema: {
    // distance of objects from camera.
    objectDistance: {type: 'number', default: 1},
    // gap between objects.
    objectGap: {type: 'number', default: 30},
    // whether or not to implement an "enlarge on hover" effect
    hover: {type: 'boolean', default: true},
    // Whether to lay objects out ically, or in a line.
    // if set to False, objects are arranged in a line, and object gap is in m, not degrees.
    // It still represents the center-to-center object gap.
    cylindrical: {type: 'boolean', default: true},
  },

  init() {
    console.log('initialized objectSet')

    // create list of children (only include "real" children - with an object3D)
    this.childrenArray = Array.from(this.el.children)
      .filter(child => child.object3D)
  },

  update() {
    // set positions of child objects based on position and distance.
    // also set animations on these child objects.
    // only include children that have an actual position.
    const midPoint = (this.childrenArray.length - 1) / 2

    if (this.data.cylindrical) {
      this.childrenArray.forEach((child, index) => {
        const degrees = (index - midPoint) * this.data.objectGap

        child.setAttribute('cylindrical-position', {
          height: 0,
          radius: this.data.objectDistance,
          angle: degrees,
          faceInward: true,
        })
        this.createAnimations(child, index)
      })
    } else {
      // straight line.
      this.childrenArray.forEach((child, index) => {
        const xpos = (index - midPoint) * this.data.objectGap

        child.setAttribute('position', {
          x: xpos,
          y: 0,
          z: -this.data.objectDistance,
        })
        this.createAnimations(child, index)
      })
    }
  },

  createAnimations(object, index) {

    // enlarge and revert animations used on mouseover.

    if (this.data.hover) {
      object.setAttribute('animation__enlarge',
        {
          property: 'scale',
          from: '1 1 1',
          to: '1.2 1.2 1.2',
          dur: 500,
          autoplay: false,
          startEvents: 'mouseenter',
        })
      object.setAttribute('animation__revert',
        {
          property: 'scale',
          from: '1.2 1.2 1.2',
          to: '1 1 1',
          dur: 500,
          autoplay: false,
          startEvents: 'mouseleave',
        })
    }
  },
}

// Simple component to allow positions to be specified in cylindrical co-ordinates rather than xyz.
export const cylindricalPosition =
{
  schema: {
    height: {type: 'number', default: 1.6},
    heightDelta: {type: 'number', default: 0},
    radius: {type: 'number', default: 1},
    angle: {type: 'number', default: 0},
    faceInward: {type: 'boolean', default: true},
    faceCamera: {type: 'boolean', default: false}
  },

  init() {
    this.cameraWorldPosition = new THREE.Vector3();
  },

  update() {
    const radians = Math.PI * this.data.angle / 180

    this.el.object3D.position.x = this.data.radius * Math.sin(radians)
    this.el.object3D.position.y = this.data.height + this.data.heightDelta
    this.el.object3D.position.z = -this.data.radius * Math.cos(radians)

    if (this.data.faceCamera) {
      this.cameraWorldPosition.set(0, 1.6, 0)
      this.el.object3D.lookAt(this.cameraWorldPosition);
    }
    else {
      this.el.object3D.rotation.x = 0
      this.el.object3D.rotation.y = -radians + (this.data.faceInward ? 0 : Math.PI)
      this.el.object3D.rotation.z = 0
    }
  },
}


// Component added to object to make it clickable.
// Includes customization of hit box, which may differ from the entity's actual dimensions.
// To acieve this the hit box is an invisible plane, rather than the entity itself.
export const clickable = {

  // room = selector for room to jump to
  // clickdata = external data to be passed out of A-Frame.
  // disabled = raycast detection disabled.
  // width & height of the hit box.  None supplied => use object width/height.
  // if one is specified the other must be as well.
  // height offset can be used to make the hit box slightly higher/lower than the image.
  schema: {
    clickdata: {type: 'string'},
    disabled: {type: 'boolean', default: false},
    redirect: {type: 'selector'},
    width: {type: 'number'},
    height: {type: 'number'},
    heightOffset: {type: 'number', default: 0},
    debug: {type: 'boolean', default: false},
    prompt: {type: 'selector'},
    promptHeight: {type: 'number', default: 1},
    glowLayer: {type: 'selector'}
  },

  init() {
    this.baseClass = this.el.getAttribute('class')
    this.baseClass = (this.baseClass === null ? '' : this.baseClass)

    this.onClick = this.onClick.bind(this)
    this.el.addEventListener('click', this.onClick)
    console.log('Initializing clickable A-Frame Component')

    if (this.data.width) {
      this.hitBox = document.createElement('a-plane')
      this.hitBox.setAttribute('visible', this.data.debug)
      this.hitBox.setAttribute('width', this.data.width)
      this.hitBox.setAttribute('height', this.data.height)
      this.hitBox.setAttribute('position', `0 ${this.data.heightOffset} 0`)
      this.el.appendChild(this.hitBox)
    }

    this.mouseEnter = this.mouseEnter.bind(this)
    this.mouseLeave = this.mouseLeave.bind(this)
    this.el.addEventListener("mouseenter", this.mouseEnter)
    this.el.addEventListener("mouseleave", this.mouseLeave)

    this.prompt = document.getElementById('look-around-prompt-mini')
  },

  update() {
    let el

    if (this.data.redirect) {
      el = this.data.redirect
    } else if (this.data.width) {
      el = this.hitBox
    } else {
      el = this.el
    }

    if (this.data.disabled) {
      el.setAttribute('class', this.baseClass)
    } else {
      el.setAttribute('class', `${this.baseClass} clickable-object`)
    }
  },

  onClick() {

    playClickSound()

    console.log(`Clicked ${this.el.id}`)

    const event = new CustomEvent('objectClicked', {detail: this.data.clickdata})
    window.dispatchEvent(event)
  },

  mouseEnter() {

    if (this.data.prompt) {
      this.prompt.setAttribute('src', `#${this.data.prompt.id}`)
      this.prompt.setAttribute('visible', true)
      this.prompt.setAttribute('width', 2.73)
      this.prompt.setAttribute('height', this.data.promptHeight)
      this.prompt.setAttribute('screen-display', "width: 24")
      const ypos = 68 + 12 * this.data.promptHeight
      this.prompt.setAttribute('screen-display', "ypos", ypos)
    }

    if (this.data.glowLayer) {
      this.data.glowLayer.setAttribute("visible", true)
    }
  },

  mouseLeave() {
    this.prompt.setAttribute('src', '#look-around-mini')
    this.prompt.setAttribute('visible', true)
    this.prompt.setAttribute('width', 1)
    this.prompt.setAttribute('height', 1)
    this.prompt.setAttribute('screen-display', "width: 12; ypos: 80")

    if (this.data.glowLayer) {
      this.data.glowLayer.setAttribute("visible", false)
    }
  }
}

// Component that can redirect mouse enter / leave events from one entity to another.
// Used in the display of videos.
// Note that despite event names, we're not really talking about the "mouse" entering / leaving
// these entities - instead what's entering / leaving the entities is an invisible reticle at the 
// center of the screen.
export const mouseRelay = {
  schema: {
    redirect: {type: 'selector'},
  },

  init() {
    this.mouseenter = this.mouseenter.bind(this)
    this.mouseleave = this.mouseleave.bind(this)
    this.el.addEventListener('mouseenter', this.mouseenter)
    this.el.addEventListener('mouseleave', this.mouseleave)
  },

  mouseenter() {
    this.data.redirect.emit('mouseenter')
  },

  mouseleave() {
    this.data.redirect.emit('mouseleave')
  },

}

/* This component provides the videos in the product rooms,
   which pop out to fill the screen when clicked.
  */
export const videoPopout = {

  // video = selector for placeholder image for when video is not shown.
  schema: {
    backupimage: {type: 'selector'},
    short: {type: 'selector'},
  },

  init() {
    this.startOfDayClick = this.startOfDayClick.bind(this);
    this.onClick = this.onClick.bind(this)
    this.el.addEventListener('click', this.onClick)
    this.videoStarted = this.videoStarted.bind(this)

    // the overall scene has the ability to request we stop all videos
    // (e.g. when we move to a new room).
    this.scene = document.querySelector('a-scene')
    this.onStopRequest = this.onStopRequest.bind(this)
    this.scene.addEventListener('stopvideos', this.onStopRequest)

    // Put the backup image in place (for pre-click display)
    // and add a "play" icon up front, and a "stop" icon (not visible).
    if (this.data.short) {
      this.backupVideo = document.createElement('a-video')
      this.backupVideo.object3D.position.z = 0.0001
      this.backupVideo.setAttribute('width', 0.561)  // !! ideally should take from parent geometry.
      this.backupVideo.setAttribute('src', `#${this.data.short.id}`)
      this.el.appendChild(this.backupVideo)

      this.introStill = document.querySelector('#intro-still')
      this.introStill.addEventListener('click', this.startOfDayClick)
      this.introStill.addEventListener('touchend', this.startOfDayClick)
    }
    else {
      this.videoPlaying = false
      this.backupImage = document.createElement('a-image')
      this.backupImage.setAttribute('src', `#${this.data.backupimage.id}`)
      this.backupImage.object3D.position.z = 0.0001
      this.backupImage.setAttribute('width', 0.561)  // !! ideally should take from parent geometry.
      this.el.appendChild(this.backupImage)
    }

    this.playButton = document.createElement('a-image')
    this.playButton.setAttribute('src', '#play-button')
    this.playButton.object3D.position.z = 0.0002
    this.playButton.object3D.position.x = 0.2
    this.playButton.object3D.position.y = -0.42
    this.playButton.setAttribute('scale', '0.1 0.1 0.1')
    //this.playButton.setAttribute('material', 'alpha-test: 0.5')
    this.el.appendChild(this.playButton)

    this.stopButton = document.createElement('a-image')
    this.stopButton.setAttribute('id', 'stop-button')
    this.stopButton.setAttribute('src', '#stop-button')
    this.stopButton.object3D.position.z = 0.0002
    this.stopButton.object3D.position.x = 0.2
    this.stopButton.object3D.position.y = -0.42
    this.stopButton.setAttribute('scale', '0.1 0.1 0.1')
    //this.stopButton.setAttribute('material', 'alpha-test: 0.5')
    this.stopButton.object3D.visible = false
    this.el.appendChild(this.stopButton)

  },


  // fires at start of day, starts the backup video playing.
  startOfDayClick() {

    if (this.backupVideo) {
      safePlay(this.backupVideo.components.material.material.map.image);
    }
  },

  stopVideo() {
    this.videoPlaying = false

    // Note, on Android we are having no luck with this.el.play() - just shows black.
    // Not sure why as docs suggest you can play the video element directly.
    // Not sure about iOS... we also have problems with videos playing there...
    const video = this.el.components.material.material.map.image
    if (!video) {
      return
    }
    video.pause()

    // this rests the video to the first frame, but does *not* reset
    // the frame currently in view.
    // however we can take care of that using the backupImage.
    video.load()

    if (this.backupImage) {
      this.backupImage.object3D.visible = true
    }
    if (this.backupVideo) {
      this.backupVideo.object3D.visible = true
    }

    this.playButton.object3D.visible = true
    this.stopButton.object3D.visible = false

    // return to normal scale.
    this.el.removeAttribute('animation__fallback')
    this.el.setAttribute('animation__fallback',
      {
        'property': 'inherit-transform.percentage',
        'to': 0,
        'dur': 2500,
        'easing': 'easeInExpo',
      })
  },

  startVideo() {
    this.videoPlaying = true

    const video = this.el.components.material.material.map.image
    if (!video) {
      return
    }

    // Play a video.
    // Note, on Android we are having no luck with this.el.play() - just shows black.
    // Not sure why as docs suggest you can play the video element directly.
    // Not sure about iOS... we also have problems with videos playing there...
    safePlay(video)

    // Remove backup image & play button.  show stop button.
    video.addEventListener('timeupdate', this.videoStarted)
    this.playButton.object3D.visible = false
    this.stopButton.object3D.visible = true

    // bring to front of screen.
    this.el.removeAttribute('animation__toscreen')
    this.el.setAttribute('animation__toscreen',
      {
        'property': 'inherit-transform.percentage',
        'to': 100,
        'dur': 2500,
        'easing': 'easeOutExpo',
      })
  },

  videoStarted() {
    if (this.backupImage) {
      this.backupImage.object3D.visible = false
    }
    if (this.backupVideo) {
      this.backupVideo.object3D.visible = false
    }

    const video = this.el.components.material.material.map.image
    video.removeEventListener('timeupdate', this.videoStarted)
  },

  onClick(event) {
    console.log(`Clicked ${this.el.id}`)
    console.log(event)
    playClickSound()

    if (this.videoPlaying) {
      this.stopVideo()
    } else {
      this.startVideo()
    }
  },

  onStopRequest(event) {
    if (this.videoPlaying) {
      this.stopVideo()
    }
  },
}

// Component to show a 3D object at a particular posiiton the screen.
// Derived from: https://github.com/diarmidmackenzie/screen-display
// xpos & ypos approximately like CSS px
// zdist is units in m.  Dfeault is 1cm.  Don't set below 0.5cm,
// or you will hit the default near clipping plane on the camera.
// (of course you can change that if you want...)
// near Camera frustum near clipping plane.	0.005
// xscale - when default is 1,
// object is scaled so that 1m takes up about 1cm on screen, 1:100 scale
export const screenDisplay = {

  schema: {
    position: {type: 'string', default: 'percent'},
    xpos: {type: 'number', default: 50},
    ypos: {type: 'number', default: 50},
    scale: {type: 'string', default: 'percent'},
    keepaspect: {type: 'boolean', default: true},
    width: {type: 'number', default: 10},
    height: {type: 'number'},
    zscale: {type: 'number'},
    zdist: {type: 'number', default: 0.01},
  },

  update() {
    // Get screen size
    // See: https://www.w3schools.com/jsref/prop_win_innerheight.asp
    this.screenwidth = window.innerWidth ||
                       document.documentElement.clientWidth ||
                       document.body.clientWidth

    this.screenheight = window.innerHeight ||
                        document.documentElement.clientHeight ||
                        document.body.clientHeight

    // Set up position, based on configuration.
    switch (this.data.position) {
      case 'percent':
        this.xpospercent = this.data.xpos
        this.ypospercent = this.data.ypos
        break

      case 'pixels':
        this.xpospercent = 100 * this.data.xpos / this.screenwidth
        this.ypospercent = 100 * this.data.ypos / this.screenheight
        break

      default:
        console.log(`Unexpected Config: ${this.data.position}`)
    }

    // Set up scale, based on configuration.
    // Swt up height & width first.
    // Then rewrite height if we need to preserve aspect ratio.
    switch (this.data.scale) {
      case 'percent':
        this.xscalepercent = this.data.width
        this.yscalepercent = this.data.height
        break

      case 'pixels':
        this.xscalepercent = 100 * this.data.width / this.screenwidth
        this.yscalepercent = 100 * this.data.height / this.screenheight
        break

      default:
        console.warn(`Unexpected Config: ${this.data.position}`)
    }

    if (this.data.keepaspect) {
      // keep aspect ratio.
      // Set height based on width.
      this.yscalepercent = this.xscalepercent * this.screenwidth / this.screenheight

      if (this.data.height) {
        console.warn('Height being ignored, since "keepaspect" is set.')
      }
    } else
    if (!this.data.height) {
      console.warn('"keepaspect" is not set, so height is required, but none provided.')
    }

    // height of FOV at zdist in meters.
    // FOV is based on height, not width:
    // https://threejs.org/docs/#api/en/cameras/PerspectiveCamera.fov
    // No idea why, but true FOV seems to be 59 degrees +/- from
    // Default FOV is supposed to be 80... ???
    const fovHeight = Math.tan(59 * Math.PI / 180) * this.data.zdist
    const fovWidth = fovHeight * this.screenwidth / this.screenheight

    // Now x position is (relPx from Center) / (width in Px) * fovWidth.
    // Similarly for y...
    // var x3DPos = xRelPx / this.width * fovWidth;
    // var y3DPos = yRelPx / this.height * fovHeight;
    const x3DPos = ((this.xpospercent / 100) - 0.5) * fovWidth
    const y3DPos = (0.5 - (this.ypospercent / 100)) * fovHeight

    this.el.object3D.position.set(x3DPos, y3DPos, -this.data.zdist)

    const x3DScale = fovWidth * this.xscalepercent / 100
    const y3DScale = fovHeight * this.yscalepercent / 100

    // zscale value depends whether keepaspect is set.
    // if it is not set, zscale parameter provides the percentage of the z-distance
    // to be used as the depth of the object.
    let z3DScale = this.data.zscale * this.data.zdist / 100
    if (this.data.keepaspect) {
      z3DScale = x3DScale
    } else if (!this.data.zscale) {
      console.warn('"keepaspect" is not set, so zscale is required, but none provided.')
    }

    this.el.object3D.scale.set(x3DScale, y3DScale, z3DScale)
  },

  tick() {
    const screenwidth = window.innerWidth ||
                        document.documentElement.clientWidth ||
                        document.body.clientWidth

    const screenheight = window.innerHeight ||
                         document.documentElement.clientHeight ||
                         document.body.clientHeight

    if ((this.screenwidth !== screenwidth) ||
        (this.screenheight !== screenheight)) {
      this.update()
    }
  },

}

// Component to blend two different 3D transforms to provide a transform for an object.
// Used to implement video "popout" from a position in the 3D scene to the main screen & back.
// Derived from: https://github.com/diarmidmackenzie/screen-display
export const inheritTransform = {

  schema: {
    objectA: {type: 'selector'},
    objectB: {type: 'selector'},
    percentage: {type: 'number', default: 0},
  },

  init() {
    this.worldMatrixA = new THREE.Matrix4()
    this.worldMatrixB = new THREE.Matrix4()
    this.localMatrixA = new THREE.Matrix4()
    this.localMatrixB = new THREE.Matrix4()
    if (!this.data.objectA) {
      console.log('Inherit Transform: ObjectA is null')
    }
    if (!this.data.objectB) {
      console.log('Inherit Transform: ObjectB is null')
    }
    console.log(`Inherit Transform... A: ${this.data.objectA.id}, B: ${this.data.objectB.id}`)
  },

  update() {
    this.alpha = this.data.percentage / 100
    this.worldMatrixA.copy(this.data.objectA.object3D.matrixWorld)
    this.worldMatrixB.copy(this.data.objectB.object3D.matrixWorld)

    this.localMatrixA.copy(this.data.objectA.object3D.matrix)
    this.localMatrixB.copy(this.data.objectB.object3D.matrix)

    this.updateTransform()
  },

  updateTransform: (function () {
    const vecA = new THREE.Vector3()
    const vecB = new THREE.Vector3()
    const quatA = new THREE.Quaternion()
    const quatB = new THREE.Quaternion()
    const parentMatrix = new THREE.Matrix4()

    return function () {
      // ensure the object is parented with the most suitable THREE.js object
      // not this parent may not be the same as the DOM parent.
      this.setMostSuitableParent()

      // To apply world position, rotation etc.to an object, we need to have the inverse
      // transform matrix of its parent
      this.el.object3D.parent.updateMatrixWorld()
      parentMatrix.copy(this.el.object3D.parent.matrixWorld)
      parentMatrix.invert()

      const object3DA = this.data.objectA.object3D
      const object3DB = this.data.objectB.object3D

      // Update Position, Scale and Quaternion as LERP / SLERP of world position,
      // scale & quaternions.
      object3DA.getWorldPosition(vecA)
      object3DB.getWorldPosition(vecB)
      this.el.object3D.position.lerpVectors(vecA,
        vecB,
        this.alpha)

      object3DA.getWorldScale(vecA)
      object3DB.getWorldScale(vecB)
      this.el.object3D.scale.lerpVectors(vecA,
        vecB,
        this.alpha)

      // This is the old version of the function - which matches current version
      // of THREE.js in A-Frame.
      object3DA.getWorldQuaternion(quatA)
      object3DB.getWorldQuaternion(quatB)
      this.el.object3D.quaternion.slerpQuaternions(quatA,
        quatB,
        this.alpha)

      // now apply the inverse of the parent matrix to the object 3D.  This should ensure that the
      // position, scale and quaternion applied above (which are all in world space) will
      // manifest correctly when the object is generated beneath a parent.
      this.el.object3D.applyMatrix4(parentMatrix)
    }
  }()),

  // This function does not update positional data for the child to reflect the new parent.
  // The calling function must take care of that - see e.g. updateTransform()
  setMostSuitableParent() {
    if (this.alpha <= 0.4) {
      // parent should be A.

      if (this.el.object3D.parent !== this.data.objectA.object3D.parent) {
        // move object to have object A as parent
        // note that removal from previous parent is automatic.
        this.data.objectA.object3D.parent.add(this.el.object3D)
      }
    } else if (this.alpha >= 0.6) {
      // parent should be B.

      if (this.el.object3D.parent !== this.data.objectB.object3D.parent) {
        // move object to have object A as parent
        // note that removal from previous parent is automatic.
        this.data.objectB.object3D.parent.add(this.el.object3D)
      }
    }

    // If we changed parent, the object position data will now be incorrect.
    // The calling function must now sort this out.
  },

  tick(time, timeDelta) {
    let update

    // we update position if:
    // alpha = 1 and object B has moved relative to parent
    // alpha = 0 and object A has moved relative to parent
    // 0 < alpha < 1 and either object has moved in world space.

    if (this.alpha === 0) {
      if (!this.localMatrixA.equals(this.data.objectA.object3D.matrix)) update = true
    } else if (this.alpha === 1) {
      if (!this.localMatrixB.equals(this.data.objectB.object3D.matrix)) {
        update = true
      }
    } else {
      // 0 < alpha < 1
      this.data.objectA.object3D.updateMatrixWorld()
      if (!this.worldMatrixA.equals(this.data.objectA.object3D.matrixWorld)) {
        this.worldMatrixA.copy(this.data.objectA.object3D.matrixWorld)
        update = true
      }

      this.data.objectB.object3D.updateMatrixWorld()
      if (!this.worldMatrixB.equals(this.data.objectB.object3D.matrixWorld)) {
        this.worldMatrixB.copy(this.data.objectB.object3D.matrixWorld)
        update = true
      }
    }

    if (update) {
      this.updateTransform()
    }
  },
}

// This adjusts the orientation component in question so that when a raycaster intersects
// with an object, the object center moves towards the raycast.
// Note that this component moves the "world" itself rather than a rig around the player.
// No particular reason for that decision -  
export const aimAssist = {
  schema: {
    raycast: {type: 'selector'},
    // Whether movement to a nnew position is quadratic or linear.
    quadratic: {type: 'boolean', default: true},
    // Once aim-assist starts to move towards an entity, this controls how nmuch movement
    // in the opposing direction is needed to break tohe "lock on" to the target.
    // This is assessed in comparison to the closest that aim-assist has got to the target so far
    // in this attempt to center on the target.
    // Set this too low, and aim-assist will get disrupted by tiny movements made by the user holdig their phone.
    // Set this too high, and aim-assist will "lock on" to a terget too aggressively, even when the user is trying to pull away.
    // Default value of 0.5 degrees seems to work pretty well in the testing we have done.
    breakLockDelta: {type: 'number', default: 0.5}  // number of degrees
  },

  init() {
    this.intersectStart = this.intersectStart.bind(this)
    this.intersectEnd = this.intersectEnd.bind(this)

    this.data.raycast.addEventListener('raycaster-intersection', this.intersectStart)
    this.data.raycast.addEventListener('raycaster-intersection-cleared', this.intersectEnd)

    this.targetPos = new THREE.Vector3()

    this.targetPosSpherical = new THREE.Spherical()
    this.intersectPosSpherical = new THREE.Spherical()

    this.camera = document.querySelector('#camera')

    // the required adjustment in yaw & pitch from the current position,
    this.requiredYaw = 0
    this.requiredPitch = 0

    // the current Yaw & Pitch
    this.currentYaw = 0
    this.currentPitch = 0

    // axes used in calculatons
    this.yAxis = new THREE.Vector3(0, 1, 0)
    this.basePitchAxis = new THREE.Vector3(1, 0, 0)
    this.pitchAxis = new THREE.Vector3(1, 0, 0)

    // used to stabilize against roll
    this.desiredPitchAxis = new THREE.Vector3()
    this.rollAdjustment = new THREE.Quaternion()

    this.adjustmentQuaternion = new THREE.Quaternion()

    this.targetElement = null

    // whether to explicitly target horizontal (as opposed to simply *not* targeting an element)
    this.targetHorizontal = true

    // used to compute drift back towards the horizontal orientation.
    this.euler = new THREE.Euler()
    this.targetHorizontalQuaternion = new THREE.Quaternion()
    this.quaternion = new THREE.Quaternion()

    // used in controlling movement (quadratic or linear)
    if (this.data.quadratic) {
      this.modifiers = {
        maxAccel : 0.5, // radians per second
        vel : 0, // initial value
        maxVel : 1
      }
    }
    else {
      this.modifiers = {
        maxAccel : 0, // radians per second
        vel : 0.5, // initial value
        maxVel : 0.5
      }
    }

    // Used to spot when user is resisting moveent towards a target - breaks target lock.
    // Don't mix vertical and horizontal (pitch and yaw) because when there's a small horizontal offset
    // but a large vertical offset, you want a small horizontal movement away to be enough to break the target lock.
    this.minYawToTarget = 0;
    this.minPitchToTarget = 0;
  },

  intersectStart(event) {
    // an intersection has started.
    // we assume just one intersection at a time, so take the first intersection

    [this.targetElement] = event.detail.els
    this.updateTargetOrientation()
    this.targetHorizontal = false
    this.minYawToTarget = Math.abs(this.requiredYaw);
    this.minPitchToTarget = Math.abs(this.requiredPitch);
  },

  intersectEnd(event) {
    // intersection ended -if there are no active intersections, stop any movement by setting
    // target orientation to be our current orientation.
    if (!this.data.raycast.components.raycaster.intersections.length) {
      this.targetElement = null
      this.updateTargetOrientation()
    }
    this.targetHorizontal = true
  },

  // For a given target, determine the orientation that would point towards it, and set that
  // for our tick processing to move towards.
  updateTargetOrientation() {
    if (this.targetElement) {
      // targetElement should have intersection data...
      const intersectPos = this.data.raycast.components.raycaster.intersections[0].point

      // and its own position (its center)
      this.targetElement.object3D.getWorldPosition(this.targetPos)

      // figure out the yaw & pitch to apply to the camera to point to this target.
      // (they will then actually be applied negatively to the world itself, or specifically
      // to the "select" component).

      // Calculate an interim position from intersection point to target point
      // which is yaw only from intersect point, and pitch only from target point.
      // i.e. has y co-ordinate of intersect point, and x & z co-ordinates of target point.
      this.targetPosSpherical.setFromVector3(this.targetPos)
      this.intersectPosSpherical.setFromVector3(intersectPos)

      this.requiredPitch = (this.targetPosSpherical.phi - this.intersectPosSpherical.phi)
      this.requiredYaw = -(this.targetPosSpherical.theta - this.intersectPosSpherical.theta)

      if (this.requiredPitch > Math.PI) this.requiredPitch -= 2 * Math.PI
      if (this.requiredPitch < -Math.PI) this.requiredPitch += 2 * Math.PI
      if (this.requiredYaw > Math.PI) this.requiredYaw -= 2 * Math.PI
      if (this.requiredYaw < -Math.PI) this.requiredYaw += 2 * Math.PI
    } else if (this.targetHorizontal) {
      // set a target Quaternion that includes only the y Rotation portion.
      this.euler.setFromQuaternion(this.el.object3D.quaternion, 'YXZ')
      this.euler.x = 0
      this.euler.z = 0
      this.targetHorizontalQuaternion.setFromEuler(this.euler)
      this.requiredPitch = 0
      this.requiredYaw = 0
    } else {
      // no target.  So target orientation is just our current orientation.
      this.requiredPitch = 0
      this.requiredYaw = 0
    }
  },

  // requred Adjustment is the *magnitude* of the adjustment
  // all numbers in these calculations are positive, no signs.
  maxRadians(requiredAdjustment, timeDelta) {

    const modifiers = this.modifiers;

    // if no adjustment is zero, current velocity is zero and no movement is needed.
    if (requiredAdjustment === 0) {
      modifiers.vel = 0;
      return 0;
    }

    //console.log("Quadratic calcs on entry...")
    //console.log(`timeDelta: ${timeDelta}, reqAdj: ${requiredAdjustment}, maxAccel: ${modifiers.maxAccel}, vel: ${modifiers.vel}, maxVel: ${modifiers.maxVel}`)

    // modifiers are maxAccel and maxVel.
    if (modifiers.maxAccel === 0) {
      // linear.
      return (modifiers.maxVel * timeDelta / 1000);
    }

    // speed up or slow down?
    // if constant decelaration at max rate would lead to an overshoot at current velocity,
    // slow down.  Else speed up.
    // note we are assuming non-zero max acceleration (zero max acceleration = linear = handled above).

    const timeToStop = modifiers.vel / modifiers.maxAccel

    // distance to stop is vt - 1/2 at^2
    const distanceToStop = Math.max(0, (modifiers.vel * timeToStop) - ((modifiers.maxAccel * timeToStop * timeToStop) / 2));

    //console.log(`distance to stop: ${distanceToStop}`)
    if (distanceToStop > requiredAdjustment) {
      // slow down.
      modifiers.vel = Math.max(0, modifiers.vel - (modifiers.maxAccel * timeDelta / 1000));
    }
    else {
      // speed up - up to speed limit of 0.5.
      modifiers.vel = Math.max(modifiers.vel, modifiers.vel + (modifiers.maxAccel * timeDelta / 1000));
    }

    // now determine max radians moveable at current velocity.
    const radians = modifiers.vel * timeDelta / 1000;

    //console.log("Quadratic calcs on exit...")
    //console.log(`Radians: ${radians}, timeDelta: ${timeDelta}, maxAccel: ${modifiers.maxAccel}, vel: ${modifiers.vel}, maxVel: ${modifiers.maxVel}`)

    return radians
  },

  checkForBreakLock() {

    const yawToTarget = Math.abs(this.requiredYaw);

    if (yawToTarget < this.minYawToTarget) {
      this.minYawToTarget = yawToTarget;
    }
    else if (yawToTarget > (this.minYawToTarget + (Math.PI * this.data.breakLockDelta / 180))) {
      // we have moved significantly away from the target since last frame.
      // this suggests the user is pulling away from the target.
      // break the lock on the target.
      console.log("Breaking target lock on Yaw movement: ", yawToTarget, this.minYawToTarget)
      this.targetElement = null
    }

    const pitchToTarget = Math.abs(this.requiredPitch);
    if (pitchToTarget < this.minPitchToTarget) {
      this.minPitchToTarget = pitchToTarget;
    }
    else if (pitchToTarget > (this.minPitchToTarget * this.data.breakLockFactor)) {
      // we have moved significantly away from the target since last frame.
      // this suggests the user is pulling away from the target.
      // break the lock on the target.
      console.log("Breaking target lock on Pitch movement: ", pitchToTarget, this.minPitchToTarget)
      this.targetElement = null
    }
  },

  // if a target exists, move towards it...
  tick(time, timeDelta) {

    this.checkForBreakLock()

    this.updateTargetOrientation()

    // default value for radians (used for movement back towards horizontal)
    var radians = timeDelta / 2000

    if (this.targetHorizontal) {
      this.el.object3D.quaternion.rotateTowards(this.targetHorizontalQuaternion, radians)
    } else {
      function minAbs(a, b) {
        if (Math.abs(a) < Math.abs(b)) {
          return a
        } else {
          return b
        }
      }

      // set pitchAxis to the axis of the current camera in the XZ (horizontal) plane.
      // we use this to correct both pitch and roll.
      this.pitchAxis.copy(this.basePitchAxis)
      this.pitchAxis.applyQuaternion(this.camera.object3D.quaternion)
      this.pitchAxis.y = 0
      this.pitchAxis.normalize()

      // now express the pitch axis in the frame of reference of the 'select' element
      // that we move for aim assist.
      this.quaternion.copy(this.el.object3D.quaternion)
      this.quaternion.invert()
      this.pitchAxis.applyQuaternion(this.quaternion)

      // consider required Yaw & Pitch together as 2 sides of a right angled triangel.
      // hypoteneuse is the total desired movement.
      const requiredYAP = Math.sqrt(this.requiredYaw * this.requiredYaw +
                                    this.requiredPitch * this.requiredPitch);

      // Given total desired movement, how much of it can we do this frame?
      // and what is that as a ratio of the tota desired movement?
      radians = this.maxRadians(requiredYAP, timeDelta)
      const ratio = radians / requiredYAP;

      // Now make an adjustment in line with that ratio, but don't ever go further than we need.
      if (this.requiredYaw !== 0) {

        const adjustment = minAbs(ratio * this.requiredYaw,
                                  this.requiredYaw)

        this.adjustmentQuaternion.setFromAxisAngle(this.yAxis,
                                                   adjustment)
        this.requiredYaw -= adjustment
        this.currentYaw += adjustment

        this.el.object3D.quaternion.multiply(this.adjustmentQuaternion)
      }

      if (this.requiredPitch !== 0) {

        const adjustment = minAbs(ratio * this.requiredPitch,
                                  this.requiredPitch)

        // and now make an adjustment on this pitch axis.
        this.adjustmentQuaternion.setFromAxisAngle(this.pitchAxis,
                                                   adjustment)
        this.requiredPitch -= adjustment
        this.currentPitch += adjustment

        this.el.object3D.quaternion.multiply(this.adjustmentQuaternion)
      }

      // finally, correct for roll..
      // where does roll come from?
      // if we pitch, and then yaw, this will lead to a level of roll.

      // simplest way to detect pitch is a y-compenent in the pitch axis.

      if (Math.abs(this.pitchAxis.y) > 0.001) {
        //console.log(`Rolling y: ${this.pitchAxis.y}`)
        //console.log(`Pitch axis: ${this.pitchAxis.x} ${this.pitchAxis.y} ${this.pitchAxis.z}`)

        this.desiredPitchAxis.copy(this.pitchAxis)
        this.desiredPitchAxis.y = 0

        // pitchAxis is already a unit vector, desiredPitchAxis maybe not.
        // this may seem backwards, but bear in mind we are rotating the world, not the camera.
        this.rollAdjustment.setFromUnitVectors(this.desiredPitchAxis.normalize(), this.pitchAxis)

        // we should be able to apply the full roll adjustment in one go - as long as
        // we do this contuinuously, it will only ever be a small adjustment.
        this.el.object3D.quaternion.multiply(this.rollAdjustment)

        // add in for extra for diagnostics
        // // recompute & log pitchAxis...
        // // set pitchAxis to the axis of the current camera in the XZ (horizontal) plane.
        // this.pitchAxis.copy(this.basePitchAxis)
        // this.pitchAxis.applyQuaternion(this.camera.object3D.quaternion)
        // this.pitchAxis.y = 0;
        // this.pitchAxis.normalize()
        //
        // // now express the pitch axis in the frame of reference of the 'select' element
        // // that we move for aim assist.
        //
        // this.quaternion.copy(this.el.object3D.quaternion)
        // this.quaternion.invert()
        // this.pitchAxis.applyQuaternion(this.quaternion)
        // console.log(`Pitch axis: ${this.pitchAxis.x} ${this.pitchAxis.y} ${this.pitchAxis.z}`)
        //
      }

      if ((this.requiredYaw === 0) &&
          (this.requiredPitch === 0)) {
        // once we lock onto the target, release it from being a target, so the user can move away.
        this.targetElement = null
      }
    }
  },
}

// Simple component that renders a polaroid as used for "looks"
export const lookPolaroid = {
  schema: {
    image: {type: 'selector'},
    caption: {type: 'selector'},
    yposition: {type: 'number', default: 0},
    clickdata: {type: 'string'},
    hover: {type: 'boolean', default: true},
    distant: {type: 'boolean', default: false}
  },

  init() {
    const distant = this.data.distant

    // the frame
    this.frame = document.createElement('a-image')
    this.frame.setAttribute('src', `#image-frame`)    
    this.frame.object3D.position.y = this.data.yposition
    this.frame.setAttribute('width', 1.4)
    this.frame.setAttribute('height', 1.4)
    this.el.appendChild(this.frame)

    // the polaroid image
    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${this.data.image.id}`)
    //this.image.setAttribute('alpha-test', 0.5)
    this.image.object3D.position.z = 0.01
    this.image.setAttribute('width', 1.25)
    this.image.setAttribute('height', 1.25)
    if (!distant) {
      this.image.setAttribute('clickable',
        {
          clickdata: this.data.clickdata,
          width: 1,
          height: 3,
          prompt: "#prompt-looks"
        })
    }
    this.frame.appendChild(this.image)


    // the white border of the polaroid (just at the bottom)
    this.plane = document.createElement('a-plane')
    //this.plane.setAttribute('alpha-test', 0.5)
    this.plane.setAttribute('material', 'shader:flat;color:white')
    this.plane.object3D.position.z = 0.01
    this.plane.object3D.position.y = -0.575
    this.plane.setAttribute('height', 0.1)
    this.plane.setAttribute('width', 1.25)
    this.image.appendChild(this.plane)
        
    // the caption
    this.caption = document.createElement('a-image')
    //this.caption.setAttribute('alpha-test', 0.5)
    this.caption.setAttribute('src', `#${this.data.caption.id}`)
    this.caption.setAttribute('width', 0.449)
    this.caption.setAttribute('height', 0.04)
    this.caption.object3D.position.z = 0.01
    this.plane.appendChild(this.caption)

    if (!distant) {
      this.createAnimations(this.el)
    }
  },

  createAnimations(object) {

    // enlarge and revert animations used on mouseover.
    if (this.data.hover) {
      object.setAttribute('animation__enlarge',
        {
          property: 'scale',
          from: '1 1 1',
          to: '1.2 1.2 1.2',
          dur: 500,
          autoplay: false,
          startEvents: 'mouseenter',
        })
      object.setAttribute('animation__revert',
        {
          property: 'scale',
          from: '1.2 1.2 1.2',
          to: '1 1 1',
          dur: 500,
          autoplay: false,
          startEvents: 'mouseleave',
        })
    }
  }
}

// Simple component that renders the products that appear in the main room
// These provide links to the product rooms.
export const productPicker = {
  schema: {
    image: {type: 'selector'},
    caption: {type: 'selector'},
    clickdata: {type: 'string'},
    scale: {type: 'number', default: 1},
    glow: {type: 'selector'},
    // scale of glow relative to image
    glowScaleH: {type: 'number', default: 1},
    glowScaleV: {type: 'number', default: 1},
    clickScaleH: {type: 'number', default: 1},
    clickScaleV: {type: 'number', default: 1},
  },

  init() {
    // the product

    this.startglow = this.startglow.bind(this)
    this.endglow = this.endglow.bind(this)

    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${this.data.image.id}`)
    this.image.setAttribute('alpha-test', 0.5)
    this.image.setAttribute('scale',
      {
        x: this.data.scale,
        y: this.data.scale,
        z: this.data.scale,
      })
    this.image.setAttribute('clickable',
      {
        clickdata: this.data.clickdata,
        width: this.data.clickScaleH,
        height: this.data.clickScaleV,
        prompt: "#prompt-products",
        promptHeight: 1.27
      })
    // keep bases of images algned even when scaled.
    this.image.object3D.position.y = this.data.scale / 2 - 0.5
    this.el.appendChild(this.image)

    // the glow image behind the product - child of image for simple positioning.
    if (this.data.glow) {
      this.glow = document.createElement('a-image')
      this.glow.setAttribute('src', `#${this.data.glow.id}`)
      this.glow.setAttribute('alpha-test', 0.001)
      this.glow.setAttribute('opacity', 0)
      this.glow.setAttribute('width', 2 * this.data.glowScaleH)
      this.glow.setAttribute('height', 2 * this.data.glowScaleV)

      // position the glow just behind the object.

      this.glow.object3D.position.z = -0.01
      this.image.appendChild(this.glow)

      this.el.addEventListener('mouseenter', this.startglow)
      this.el.addEventListener('mouseleave', this.endglow)
    }

    // Products rotated to face towards the camera (about the y axis only).
    this.cameraWorldPosition = new THREE.Vector3();
    this.cameraWorldPosition.set(0, 1, 0)
    this.el.object3D.lookAt(this.cameraWorldPosition);
    this.el.object3D.rotation.x = 0;
    this.el.object3D.rotation.z = 0;

  },

  startglow() {
    this.glow.removeAttribute('animation__glow')
    this.glow.removeAttribute('animation__glow_scale')
    this.glow.setAttribute('animation__glow',
      {
        property: 'opacity',
        from: 0,
        to: 1,
        dur: 500,
        autoplay: true,
      })
    this.glow.setAttribute('animation__glow_scale',
      {
        property: 'scale',
        from: "0 0 0",
        to: "1 1 1",
        dur: 500,
        autoplay: true,
      })
  },

  endglow() {
    this.glow.removeAttribute('animation__endglow')
    this.glow.removeAttribute('animation__endglow_scale')
    this.glow.setAttribute('animation__endglow',
      {
        property: 'opacity',
        from: 1,
        to: 0,
        dur: 500,
        autoplay: true,
      })

    this.glow.setAttribute('animation__endglow_scale',
      {
        property: 'scale',
        from: "1 1 1",
        to: "0 0 0",
        dur: 500,
        autoplay: true,
      })
  },
}

// Simple component that renders the product picker that appears next to the 
// looks in the looks rooms.
// very similar (but with different params) to productProducts.  If fixing bugs in one, check the
// other too...
export const lookProducts = {
  schema: {
    image: {type: 'selector'},
    caption: {type: 'selector'},
    products: {type: 'array'},
  },

  init() {
    // the 'look' image
    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${this.data.image.id}`)
    //this.image.setAttribute('alpha-test', 0.5)

    // image is offset to the left.
    this.image.object3D.position.x = -0.6
    this.el.appendChild(this.image)

    // the caption at the bottom
    this.caption = document.createElement('a-image')
    //this.caption.setAttribute('alpha-test', 0.5)
    this.caption.setAttribute('src', `#${this.data.caption.id}`)
    // Captions are white, 593 x 51px
    this.caption.setAttribute('width', 0.8)
    this.caption.setAttribute('height', 0.0688)
    this.caption.object3D.position.y = -0.7
    this.el.appendChild(this.caption)
    
    this.products = new Array(this.data.products.length)
    this.data.products.forEach((product, index) => {
      this.products[index] = document.createElement('a-entity')
      this.products[index].setAttribute('product-panel',
        {'index': product,
         'clickable': false})
      // each product takes up 0.25m vertically and they are centered at y = 0.
      this.products[index].object3D.position.y = 0.25 * (this.data.products.length / 2 - index - 0.5)
      this.products[index].object3D.position.x = 0.5
      this.el.appendChild(this.products[index])
    })
  },
}

// Simple component that renders the product picker that appears next to the 
// products in the product rooms.
// very similar (but with different params) to lookProducts.  If fixing bugs in one, check the
// other too...
export const productProducts = {
  schema: {
    image: {type: 'selector'},
    imagescale: {type: 'number'},
    caption: {type: 'selector'},
    // Default offset works for 1x1 images
    imageoffset: {type: 'number', default: -0.6},
    imageRotation: {type: 'number', default: 0},
    listLeftPadding: {type: 'number', default: 0},
    productlistoffset: {type: 'number', default: 0},
    products: {type: 'array'},
  },

  init() {
    this.productClicked = this.productClicked.bind(this);
    this.el.addEventListener("productClicked", this.productClicked);

    // the default 'pack' image
    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${this.data.image.id}`)
    this.image.setAttribute('alpha-test', 0.5)
    this.image.setAttribute('scale', {
      x: this.data.imagescale,
      y: this.data.imagescale,
      z: this.data.imagescale,
    })
    this.image.object3D.rotation.z = THREE.MathUtils.degToRad(this.data.imageRotation)
    
    // image is offset to the left.
    this.image.object3D.position.x = this.data.imageoffset
    this.el.appendChild(this.image)

    // the caption at the bottom
    this.caption = document.createElement('a-image')
    //this.caption.setAttribute('alpha-test', 0.5)
    this.caption.setAttribute('src', `#${this.data.caption.id}`)
    this.caption.setAttribute('width', 1.5)
    this.caption.setAttribute('height', 0.0625)
    // y height allows for 6 elements in product list without overlapping the caption
    this.caption.object3D.position.y = -0.95
    this.el.appendChild(this.caption)

    this.products = new Array(this.data.products.length)
    this.data.products.forEach((product, index) => {
      this.products[index] = document.createElement('a-entity')
      this.products[index].setAttribute('product-panel',
        {index: product,
         imageLeftOffset: this.data.listLeftPadding})
      // each product takes up 0.25m vertically and they are centered at y = 0.
      this.products[index].object3D.position.y = 0.25 * (this.data.products.length / 2 - index - 0.5)
      this.products[index].object3D.position.x = 0.5 + this.data.productlistoffset
      this.el.appendChild(this.products[index])
    })
  },

  productClicked(event) {

    // product index is reported on the event.
    const product = constants.PRODUCTS[event.detail.productIndex]
    // product pack image asset IDs are of the form <product-name>-pack
    this.image.setAttribute('src', `#${product.image}-pack`)
  }
}

// Component that renders an individual product panel, as used in either product-products
// or look-products.
export const productPanel = {
  // index is the index into constants.PRODUCTS of the product.
  schema: {
    index: {type: 'number'},
    clickable: {type: 'boolean', default: true},
    imageLeftOffset: {type: 'number', default: 0},
    style: {type: 'string', default: 'plane', oneOf:['plane', 'image']},
    // only valid where style = image.
    imageWidth: {type: 'number', default: 1},
    imageHeight: {type: 'number', default: 1},
    captionXPos: {type: 'number', default: 0},
  },

  init() {
    // register for updates to cart.
    this.cartChanged = this.cartChanged.bind(this)
    this.scene = document.querySelector('a-scene')
    this.scene.addEventListener('cartchanged', this.cartChanged)

    // amd track the place where we can get stock level data.
    this.dataDiv = document.querySelector('#aframe-scene-data')


    if (this.data.style === 'plane') {
      this.createPlaneStyleEntities()
    }
    else {
      this.createImageStyleEntities()
    }
  },

  createPlaneStyleEntities() {
  
    // the white background of the product panel
    this.plane = document.createElement('a-plane')
    //this.plane.setAttribute('alpha-test', 0.5)
    this.plane.setAttribute('material', 'shader:flat;color:white')
    this.plane.setAttribute('height', 0.24)
    if (this.data.clickable) {
      this.plane.setAttribute('click-to-show-product', `productIndex:${this.data.index}; disabled:true`)
    }
    this.el.appendChild(this.plane)

    // the product image on the left
    const product = constants.PRODUCTS[this.data.index]
    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${product.image}`)
    //this.image.setAttribute('alpha-test', 0.5)
    this.image.setAttribute('width', 0.2)
    this.image.setAttribute('height', 0.2)
    // image is offset to the left.
    this.image.object3D.position.x = this.data.imageLeftOffset - 0.4 
    this.image.object3D.position.z = 0.001
    this.plane.appendChild(this.image)

    this.caption = document.createElement('a-text')
    //this.caption.setAttribute('alpha-test', 0.5)
    
    this.caption.setAttribute('value', `${product.name}`)
    this.caption.setAttribute('color', 'black')
    this.caption.setAttribute('font', 'UniversLTStd-BoldCn-msdf.json')
    this.caption.setAttribute('negate', false)
    this.caption.setAttribute('width', 0.6)
    this.caption.setAttribute('align', 'center')
    this.caption.setAttribute('wrap-count', 24)
    this.caption.object3D.position.z = 0.001
    this.plane.appendChild(this.caption)

    // the cart icon on the right
    this.cart = document.createElement('a-image')
    //this.cart.setAttribute('alpha-test', 0.5)
    this.cart.setAttribute('width', 0.2)
    this.cart.setAttribute('height', 0.2)
    // if clickable, must be set up as disabled initially (will be enabled when room is entered)
    this.cart.setAttribute('click-to-add-to-cart', 'disabled:true')
    this.cart.setAttribute('click-to-show-product', `productIndex:${this.data.index}; disabled:true`)
    this.cart.object3D.position.z = 0.001
    this.cart.object3D.position.x = 0.5
    this.setIconFromStockLevels()

    this.plane.appendChild(this.cart)
  },

  // "Image Style" - Was implemented for Palette products.  No longer used.
  createImageStyleEntities() {
  
    // the product image on the left
    const product = constants.PRODUCTS[this.data.index]
    this.image = document.createElement('a-image')
    this.image.setAttribute('src', `#${product.image}`)
    //this.image.setAttribute('alpha-test', 0.5)
    this.image.setAttribute('width', this.data.imageWidth)
    this.image.setAttribute('height', this.data.imageHeight)
    
    this.el.appendChild(this.image)

    this.caption = document.createElement('a-text')
    //this.caption.setAttribute('alpha-test', 0.5)
    this.caption.setAttribute('value', `${product.name}`)
    this.caption.setAttribute('color', 'black')
    this.caption.setAttribute('font', 'UniversLTStd-BoldCn-msdf.json')
    this.caption.setAttribute('negate', false)
    this.caption.setAttribute('width', 0.6)
    this.caption.setAttribute('align', 'center')
    this.caption.setAttribute('wrap-count', 24)
    this.caption.object3D.position.z = 0.001
    this.caption.object3D.position.x = this.data.captionXPos;
    this.caption.object3D.position.y = -this.data.imageHeight / 2 - 0.02
    this.el.appendChild(this.caption)

    // the cart icon on the right
    this.cart = document.createElement('a-image')
    //this.cart.setAttribute('alpha-test', 0.5)
    this.cart.setAttribute('width', 0.2)
    this.cart.setAttribute('height', 0.2)
    // if clickable, must be set up as disabled initially (will be enabled when room is entered)
    this.cart.setAttribute('click-to-add-to-cart', 'disabled:true')
    this.cart.setAttribute('click-to-show-product', `productIndex:${this.data.index}; disabled:true`)
    this.cart.object3D.position.z = 0.001
    this.cart.object3D.position.x = (this.data.imageWidth / 2) - 0.1
    this.cart.object3D.position.y = -this.data.imageHeight / 2
    this.setIconFromStockLevels()

    this.el.appendChild(this.cart)
  },

  setIconFromStockLevels() {
    // only work to do if cart is initialized.
    if (!this.cart) return

    // default -1 means "no information" - in which case we assume stock available.
    let availableCount = -1

    if (this.dataDiv &&
        this.dataDiv.dataset &&
        this.dataDiv.dataset.stock) {
      availableCount = Number(this.dataDiv.dataset.stock.split(',')[this.data.index])
    }

    if (availableCount === 0) {
      // we know how many available, and it's none.
      // put up out-of-stock sign.
      this.cart.setAttribute('src', '#out-of-stock-icon')
      this.cart.removeAttribute('click-to-add-to-cart')
    } else {
      this.cart.setAttribute('src', '#product-basket-icon')
      this.cart.setAttribute('click-to-add-to-cart', `productIndex:${this.data.index}`)
    }
  },

  // when cart contents change, make sure we are up to date on what is in stock.
  cartChanged() {
    this.setIconFromStockLevels()
  },
}

// Component used to ensure product displayed to the left of the product picker
// is updated when a product is clicked on.
// This doesn't actually update the product image, but emits a "productClicked"
// event that is detected in the "product-products" component that renders the overall
// display.
export const clickToShowProduct = {
  schema: {
    productIndex: {type: 'number'},
    disabled: {type: 'boolean'},
  },

  init() {
    this.onClick = this.onClick.bind(this)
    this.el.addEventListener('click', this.onClick)

  },

  update() {
    if (this.data.disabled) {
      this.el.setAttribute('class', this.baseClass)
    } else {
      this.el.setAttribute('class', `${this.baseClass} clickable-item`)
    }
  },

  onClick() {

    playClickSound()

    // emit here and rely on bubbling to reach the parent.
    this.el.emit("productClicked", {productIndex: this.data.productIndex})
  }
}

// Component that (on click) generates an event indicating a product is to be added to
// the cart.
export const clickToAddToCart = {
  schema: {
    productIndex: {type: 'number'},
    disabled: {type: 'boolean'},
  },

  init() {
    this.scene = document.querySelector('a-scene')
    this.baseClass = this.el.getAttribute('class')
    this.baseClass = (this.baseClass === null ? '' : this.baseClass)

    this.onClick = this.onClick.bind(this)
    this.el.addEventListener('click', this.onClick)
  },

  update() {
    if (this.data.disabled) {
      this.el.setAttribute('class', this.baseClass)
    } else {
      this.el.setAttribute('class', `${this.baseClass} clickable-item`)
    }
  },

  onClick() {

    playClickSound()

    // send a message to the React layer to add to cart.
    const event = new CustomEvent('addToCart', {detail: this.data.productIndex})
    window.dispatchEvent(event)

    // also give a hint to the user that they just clicked something.
    this.el.setAttribute('scale', '0.8 0.8 0.8')
    setTimeout(() => this.el.setAttribute('scale', '1 1 1'), 200)

    // tell the scene that the cart has changed
    // listeners will update their icons to reflect stock levels.
    this.scene.emit('cartchanged')
  },

}

// Simple component to render a benefits panel.
export const benefitsPanel = {
  schema: {
    border: {type: 'selector', default: '#benefits-border'},
    benefits: {type: 'selector'},
    fitIntoFrame: {type: 'boolean', default: true}
  },

  init() {

    this.border = document.createElement('a-image')
    this.border.setAttribute('src', `#${this.data.border.id}`)
    this.border.setAttribute('width', 1)
    this.border.setAttribute('height', 1)
    this.el.appendChild(this.border)
    
    this.benefits = document.createElement('a-image')
    this.benefits.setAttribute('src', `#${this.data.benefits.id}`)
    this.benefits.setAttribute('material', 'transparent: true')
    if (this.data.fitIntoFrame) {
      this.benefits.setAttribute('width', 0.9)
      this.benefits.setAttribute('height', 0.9)
    }
    else {
      this.benefits.setAttribute('width', 1)
      this.benefits.setAttribute('height', 1)
    }
    
    this.benefits.setAttribute('position', '0 0 0.001')
    this.el.appendChild(this.benefits)
  },
}

// Top-level component that displays a video with popout function.
// Depends upon video-poput and inherit-transform components
// declared earlier in this module.
export const videoDisplay = {
  schema: {
    video: {type: 'selector'},
    still: {type: 'selector'},
    short: {type: 'selector'}, // if used, prioritize over still.
    caption: {type: 'selector'},
    border: {type: 'selector', default: '#video-border'},
  },

  init() {
    // the placeholder for the video (from which it pops out)
    this.plane = document.createElement('a-plane')
    this.plane.setAttribute('id', `${this.data.video.id}-position`)
    this.plane.setAttribute('visible', false)
    this.plane.setAttribute('clickable', `redirect:#${this.data.video.id}-entity;disabled:true`)
    this.plane.setAttribute('width', 0.561)
    this.el.appendChild(this.plane)

    // caption for the video (appears below)

    // the caption below the video panel
    // caption PNG is 426 x 51px
    this.caption = document.createElement('a-image')
    this.caption.setAttribute('src', `#${this.data.caption.id}`)
    //this.caption.setAttribute('alpha-test', 0.5)
    this.caption.setAttribute('width', 0.34)
    this.caption.setAttribute('height', 0.0407)
    this.caption.object3D.position.z = 0.01
    this.caption.object3D.position.y = -0.7
    this.el.appendChild(this.caption)

    // now the video pop-out itself.
    this.video = document.createElement('a-video')
    this.video.setAttribute('id', `${this.data.video.id}-entity`)
    this.video.setAttribute('src', `#${this.data.video.id}`)
    this.video.setAttribute('width', 0.561)
    if (this.data.short) {
      this.video.setAttribute('video-popout', `short: #${this.data.short.id}`)
    }
    else {
      this.video.setAttribute('video-popout', `backupimage: #${this.data.still.id}`)
    }

    this.video.setAttribute('inherit-transform',
      {
        'objectA': `#${this.data.video.id}-position`,
        'objectB': '#screenVideoAnchor',
        'percentage': 0,
      })

    // relay mouse enter and mouse leave events to base position of video, so that
    // we scale this up/down, rather than the video itself.
    this.video.setAttribute('mouse-relay', `redirect:#${this.data.video.id}-position`)
    this.el.appendChild(this.video)

    // border behind video.  A child of the video to minimize change of popout mechanism.
    this.border = document.createElement('a-image')
    this.border.setAttribute('src', `#${this.data.border.id}`)
    this.border.setAttribute('height', 1.1)
    this.border.setAttribute('width', 0.661)
    this.border.setAttribute('position', "0 0 -0.01")
    this.video.appendChild(this.border)

  },
}

export const balloonAnimation = {

  schema: {
    index: {type: 'number' }
  },

  init() {

    var duration
    var variation

    switch (this.data.index) {
      case 1:
        duration = 10000
        variation = 10
        break;

      case 2:
        duration = 8363
        variation = 11
        break;

// 3, 4 & 5 move together to avoid overlaps
      case 3:
        duration = 11000
        variation = 2
        break;

      case 4:
        duration = 11000
        variation = 4
        break;

      case 5:
        duration = 11000
        variation = 8
        break;

      case 6:
        duration = 9323
        variation = 9
        break;

      case 7:
          duration = 13113
          variation = 10
          break;

      case 8:
        duration = 11000
        variation = 12
        break;

      case 9:
        duration = 10010
        variation = 11
        break;
        
      case 10:
        duration = 9700
        variation = 10
        break;

      default:
        console.log(`error, unexpected index: ${this.data.index}`)
        break;
    }

    this.el.setAttribute("animation__up", 
                          `property: spherical-position.heightDelta;
                            to: ${variation};
                            dur: ${duration};
                            easing: easeInOutQuad;
                            startEvents: animationcomplete__down`)

    this.el.setAttribute("animation__down", 
                          `property: spherical-position.heightDelta;
                            to: ${0};
                            dur: ${duration};
                            easing: easeInOutQuad;
                            startEvents: animationcomplete__up`)

    // kick things off...
    window.addEventListener("introVideoEnded", () => {
      this.el.emit("animationcomplete__down")
    });
  }
}

export const sphericalPosition =
{
  schema: {
    height: {type: 'number', default: 1.6},
    heightDelta: {type: 'number', default: 0},
    radius: {type: 'number', default: 1},
    angle: {type: 'number', default: 0},
  },

  init() {
    this.cameraWorldPosition = new THREE.Vector3();
    this.spherical = new THREE.Spherical()
  },

  update() {
    const radians = Math.PI * this.data.angle / 180
    this.spherical.phi = THREE.MathUtils.degToRad(90 - this.data.height - this.data.heightDelta)
    this.spherical.theta = THREE.MathUtils.degToRad(180 - this.data.angle)
    this.spherical.radius = this.data.radius

    this.el.object3D.position.setFromSpherical(this.spherical)
    this.cameraWorldPosition.set(0, 1.6, 0)
    this.el.object3D.lookAt(this.cameraWorldPosition);

  },
}

